/**
 * BrandingContext — R3-Sprint5 前端共享接缝。
 *
 * 在 App 顶层包裹整个应用:
 * - 启动时解析 window.location.host → tenant_slug(子域)
 * - 若有 slug,调 /public/branding/{slug} 拉品牌信息(无 JWT)
 * - 失败/无 slug → 使用 default branding(平台主品牌)
 * - 把 PublicBranding 注入 React Context,任何组件都可 useBranding() 获取
 * - 同时把 primary_color 喂给 antd ConfigProvider(主题色应用全局)
 *
 * 子门户 UX 路径:
 *   demo.incubator.iaiot.cloud
 *     → parseSubdomainSlug 返 'demo'
 *     → getPublicBranding('demo')
 *     → ConfigProvider primaryColor=branding.primary_color
 *     → favicon / login bg / logo 等都从 context 读
 *
 * 主域路径:
 *   incubator.iaiot.cloud
 *     → parseSubdomainSlug 返 null
 *     → useDefaultBranding(无网络请求)
 *     → 平台默认蓝主题
 */

import { ConfigProvider, theme } from 'antd';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  getPublicBranding,
  getReservedSubdomainsCached,
  parseSubdomainSlug,
  type PublicBranding,
  type ReservedSubdomainsResponse,
} from '@/api/branding';

const _DEFAULT_BRANDING: PublicBranding = {
  tenant_slug: '__default__',
  company_name: '锚点科技 · 孵化器一体化运营平台',
  logo_url: null,
  favicon_url: null,
  primary_color: '#2266FF', // 与 CLAUDE.md 品牌色对齐(主蓝)
  login_bg_url: null,
  font_family: null,
  tagline: '锚定确定性 · 解析无限可能',
  icp_filing: null,
};

interface BrandingContextValue {
  branding: PublicBranding;
  /** 是否在子域(子门户模式)— 主域 false */
  isSubdomain: boolean;
  /** 是否正在拉品牌(可用于显 spinner)*/
  loading: boolean;
  /** 解析失败的错误(slug 不存在 / 网络错)*/
  error: string | null;
  /**
   * R3-Sprint6 Sec I1 — 后端权威 RESERVED 子域 + slug regex。
   * - null:启动期未拉到 / 网络失败(消费方应降级 fallback)
   * - 拉到后通知所有 useBranding() 消费方
   * 注意:此字段独立于 `branding`/`error` — RESERVED 失败不影响 branding 渲染。
   */
  reserved: ReservedSubdomainsResponse | null;
}

const _Context = createContext<BrandingContextValue>({
  branding: _DEFAULT_BRANDING,
  isSubdomain: false,
  loading: false,
  error: null,
  reserved: null,
});

export function useBranding(): BrandingContextValue {
  return useContext(_Context);
}

interface BrandingProviderProps {
  children: ReactNode;
}

export function BrandingProvider({ children }: BrandingProviderProps) {
  // R3-Sprint6 Sec I1:RESERVED 子域 + slug regex(独立于 branding,失败彼此隔离)
  const [reserved, setReserved] = useState<ReservedSubdomainsResponse | null>(null);

  // QA Reviewer H2 修(HIGH):slug 必须依赖 reserved — 否则 useEffect 只在 mount
  // 时用本地 _RESERVED_FALLBACK 解析,后端拉到的 RESERVED 永远不消费,Sec I1 dedup 无效。
  // 启动期先用 fallback 解析(reserved=null);RESERVED 拉到后 useMemo 触发重算 →
  // 若 backend 加了新词且当前 sub 命中 → slug 由 string 变 null → 下方 branding useEffect
  // 触发 cancel 重跑(slug 变成 null 时 early return,branding 留 default)。
  const subSlug = useMemo(
    () =>
      parseSubdomainSlug(
        reserved ? new Set(reserved.reserved) : undefined,
      ),
    [reserved],
  );

  // R3-Sprint12-A 用户反馈修:主域访问场景(非子域)+ 用户在 Header 切了租户/登录后,
  // BrandingContext 也要追上 localStorage.tenant_code,否则永远 fallback _DEFAULT。
  // 监听 storage 事件(跨标签)+ 自定义 'tenant-code-changed'(同标签 setTenantCode 触发)。
  const [tenantOverride, setTenantOverride] = useState<string | null>(
    () => localStorage.getItem('tenant_code'),
  );
  useEffect(() => {
    const sync = () => setTenantOverride(localStorage.getItem('tenant_code'));
    window.addEventListener('storage', sync);
    window.addEventListener('tenant-code-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('tenant-code-changed', sync);
    };
  }, []);

  // 白标保存 / 改 logo 后主动 refetch — TenantThemePage 触发 'branding-refresh' 事件
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    const bump = () => setRefreshTick((t) => t + 1);
    window.addEventListener('branding-refresh', bump);
    return () => window.removeEventListener('branding-refresh', bump);
  }, []);

  // 子域优先,无则走 localStorage tenant_code(主域 + 选过租户)
  const slug = subSlug ?? tenantOverride;
  const isSubdomain = subSlug !== null;

  const [branding, setBranding] = useState<PublicBranding>(_DEFAULT_BRANDING);
  const [loading, setLoading] = useState<boolean>(isSubdomain);
  const [error, setError] = useState<string | null>(null);

  // fire-and-forget:启动期拉一次后端 RESERVED — 失败静默(降级走 _RESERVED_FALLBACK)
  useEffect(() => {
    let cancelled = false;
    getReservedSubdomainsCached()
      .then((r) => {
        if (!cancelled) setReserved(r);
      })
      .catch(() => {
        // 静默:消费方未拿到 reserved 时走本地 _RESERVED_FALLBACK,不影响 branding 主流程
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    getPublicBranding(slug)
      .then((b) => {
        if (cancelled) return;
        setBranding(b);
        // 同步应用 favicon(若有)— DOM 直接改
        // QA Sec L1 修(LOW):favicon 仅接受 http(s) — 浏览器不识 oss://s3:// 协议会静默失败,
        // 同时防 javascript:/data: 协议(后端 _validate_url 已守,前端深度防御)
        if (b.favicon_url && /^https?:\/\//i.test(b.favicon_url)) {
          const link = (document.querySelector("link[rel~='icon']") ||
            document.createElement('link')) as HTMLLinkElement;
          link.rel = 'icon';
          link.href = b.favicon_url;
          if (!link.parentNode) document.head.appendChild(link);
        }
        // 同步 title
        if (b.company_name) {
          document.title = b.company_name;
        }
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
        // 解析失败 → 降级用 default(不阻塞 app 启动)
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, refreshTick]);

  const ctxValue = useMemo(
    () => ({ branding, isSubdomain, loading, error, reserved }),
    [branding, isSubdomain, loading, error, reserved],
  );

  // antd ConfigProvider — primary_color + font_family 应用到全局组件
  // R3-Sprint12-A-UX7:font_family 真接通(原仅 schema 校验,不消费)
  const themeConfig = useMemo(() => {
    const t: Record<string, unknown> = {
      colorPrimary: branding.primary_color ?? _DEFAULT_BRANDING.primary_color!,
    };
    if (branding.font_family && branding.font_family.trim()) {
      // 简单去毒:同 TenantThemePage 范式,deny CSS 终止符防 inline-style injection
      const safeFont = !/[;{}<>()@"']/.test(branding.font_family)
        ? branding.font_family.slice(0, 64)
        : null;
      if (safeFont) t.fontFamily = safeFont;
    }
    return { algorithm: theme.defaultAlgorithm, token: t };
  }, [branding.primary_color, branding.font_family]);

  // R3-Sprint12-A-UX7:font_family 也写到 <html> 上,覆盖非 antd 元素(标题/纯文本)
  useEffect(() => {
    if (branding.font_family && !/[;{}<>()@"']/.test(branding.font_family)) {
      document.documentElement.style.setProperty(
        '--brand-font-family',
        branding.font_family.slice(0, 64),
      );
    } else {
      document.documentElement.style.removeProperty('--brand-font-family');
    }
  }, [branding.font_family]);

  return (
    <_Context.Provider value={ctxValue}>
      <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>
    </_Context.Provider>
  );
}
